import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  FaSyncAlt,
  FaShareAlt,
  FaEye,
  FaUsers,
  FaAward,
  FaBullhorn,
  FaSearch,
  FaUndo
} from "react-icons/fa";
import { fetchWithAuth } from "../../utils/api";
import "./EventSharingAnalytics.css";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DATE_RANGE_OPTIONS = [
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "Last 90 Days", value: "90d" },
  { label: "This Year", value: "year" },
  { label: "All Time", value: "all" },
  { label: "Custom Range", value: "custom" }
];

const PLATFORM_COLORS = {
  linkedin: "#0a66c2",
  whatsapp: "#25d366",
  instagram: "#e4405f",
  facebook: "#1877f2",
  x: "#0284c7",
  threads: "#a855f7",
  email: "#ea4335",
  copy_link: "#f59e0b",
  direct: "#64748b",
  other: "#94a3b8"
};

function EventSharingAnalytics() {
  // Filters State
  const [dateRange, setDateRange] = useState("30d");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [selectedMedium, setSelectedMedium] = useState("all");

  // Dynamic filter options
  const [filterOptions, setFilterOptions] = useState({
    events: [],
    platforms: [],
    campaigns: [],
    sources: [],
    mediums: []
  });

  // Data State
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({ totalRows: 0, page: 1, limit: 10, totalPages: 1 });

  // UI State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const sortBy = "landingVisits";
  const sortOrder = "desc";
  const [platformChartMetric, setPlatformChartMetric] = useState("all");

  // Date range bounds
  const getDateBounds = useCallback(() => {
    const now = new Date();
    let start = null;
    let end = now.toISOString().split("T")[0];

    if (dateRange === "7d") {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      start = d.toISOString().split("T")[0];
    } else if (dateRange === "30d") {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      start = d.toISOString().split("T")[0];
    } else if (dateRange === "90d") {
      const d = new Date();
      d.setDate(d.getDate() - 90);
      start = d.toISOString().split("T")[0];
    } else if (dateRange === "year") {
      const d = new Date(now.getFullYear(), 0, 1);
      start = d.toISOString().split("T")[0];
    } else if (dateRange === "custom") {
      start = customStartDate || null;
      end = customEndDate || null;
    }

    return { startDate: start, endDate: end };
  }, [dateRange, customStartDate, customEndDate]);

  // Build query string strictly for Event context
  const buildQueryParams = useCallback(
    (extra = {}) => {
      const { startDate, endDate } = getDateBounds();
      const params = new URLSearchParams();
      params.set("contentType", "event");

      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (selectedEvent !== "all") params.set("eventId", selectedEvent);
      if (selectedPlatform !== "all") params.set("platform", selectedPlatform);
      if (selectedCampaign !== "all") params.set("campaign", selectedCampaign);
      if (selectedSource !== "all") params.set("source", selectedSource);
      if (selectedMedium !== "all") params.set("medium", selectedMedium);

      Object.entries(extra).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
          params.set(k, v);
        }
      });

      return params.toString();
    },
    [getDateBounds, selectedEvent, selectedPlatform, selectedCampaign, selectedSource, selectedMedium]
  );

  const fetchFilterOptions = async () => {
    try {
      const res = await fetchWithAuth("/admin/event-share-analytics/filter-options");
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.options) {
          setFilterOptions({
            events: json.options.events || json.options.items || [],
            platforms: json.options.platforms || [],
            campaigns: json.options.campaigns || [],
            sources: json.options.sources || [],
            mediums: json.options.mediums || []
          });
        }
      }
    } catch (err) {
      console.warn("Could not fetch event filter options:", err);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    setError(null);
    try {
      const q = buildQueryParams();

      const [statsRes, chartsRes] = await Promise.all([
        fetchWithAuth(`/admin/event-share-analytics/stats?${q}`),
        fetchWithAuth(`/admin/event-share-analytics/charts?${q}`)
      ]);

      const statsData = await statsRes.json();
      const chartsData = await chartsRes.json();

      const tableQ = buildQueryParams({
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery,
        sortBy,
        sortOrder
      });
      const tableRes = await fetchWithAuth(`/admin/event-share-analytics/table?${tableQ}`);
      const tableJson = await tableRes.json();

      if (statsData.success) setStats(statsData.stats);
      if (chartsData.success) setCharts(chartsData.charts);
      if (tableJson.success) {
        setTableData(tableJson.data || []);
        setPagination((prev) => ({
          ...prev,
          totalRows: tableJson.pagination?.totalRows || 0,
          totalPages: tableJson.pagination?.totalPages || 1
        }));
      }
    } catch (err) {
      console.error("Error fetching event share analytics:", err);
      setError("Failed to load event share analytics. Please verify your connection.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildQueryParams, pagination.page, pagination.limit, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
    fetchFilterOptions();
  };

  const handleResetFilters = () => {
    setDateRange("30d");
    setCustomStartDate("");
    setCustomEndDate("");
    setSelectedEvent("all");
    setSelectedPlatform("all");
    setSelectedCampaign("all");
    setSelectedSource("all");
    setSelectedMedium("all");
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Timeline chart data
  const timelineChartData = {
    labels: charts?.trafficTrends?.map((d) => d.date) || [],
    datasets: [
      {
        label: "Social Shares",
        data: charts?.trafficTrends?.map((d) => d.shares) || [],
        borderColor: "#0284c7",
        backgroundColor: "rgba(2, 132, 199, 0.15)",
        fill: true,
        tension: 0.35,
        borderWidth: 2
      },
      {
        label: "Link Copies",
        data: charts?.trafficTrends?.map((d) => d.copies) || [],
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245, 158, 11, 0.15)",
        fill: true,
        tension: 0.35,
        borderWidth: 2
      },
      {
        label: "Campaign Landings",
        data: charts?.trafficTrends?.map((d) => d.visits) || [],
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.2)",
        fill: true,
        tension: 0.35,
        borderWidth: 2.5
      }
    ]
  };

  // Platform distribution chart
  const platformLabels = Object.keys(charts?.platformDistribution?.shares || {});
  const platformDoughnutData = {
    labels: platformLabels.map((p) => p.toUpperCase()),
    datasets: [
      {
        data: platformLabels.map((p) => {
          const s = charts?.platformDistribution?.shares?.[p] || 0;
          const v = charts?.platformDistribution?.visits?.[p] || 0;
          if (platformChartMetric === "shares") return s;
          if (platformChartMetric === "visits") return v;
          return s + v;
        }),
        backgroundColor: platformLabels.map((p) => PLATFORM_COLORS[p] || "#94a3b8"),
        borderColor: "#1e293b",
        borderWidth: 2
      }
    ]
  };

  // Event performance chart
  const eventPerfData = {
    labels: charts?.performanceList?.map((p) => (p.title?.length > 25 ? p.title.substring(0, 25) + "..." : p.title)) || [],
    datasets: [
      {
        label: "Shares",
        data: charts?.performanceList?.map((p) => p.shares) || [],
        backgroundColor: "#0284c7"
      },
      {
        label: "Landing Visits",
        data: charts?.performanceList?.map((p) => p.visits) || [],
        backgroundColor: "#10b981"
      }
    ]
  };

  return (
    <div className="share-analytics-container event-sharing-analytics-container">
      {/* HEADER */}
      <div className="share-analytics-header">
        <div className="header-text">
          <span className="analytics-badge event-badge">🎟️ Event Tracking</span>
          <h1>Event Sharing Analytics</h1>
          <p>
            Autonomous tracking across 9 UTM parameters for event shares, link copies, and campaign attendee conversion.
          </p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="action-btn refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh analytics data"
          >
            <FaSyncAlt className={refreshing ? "spinning" : ""} />
            <span>{refreshing ? "Refreshing..." : "Refresh Data"}</span>
          </button>
        </div>
      </div>

      {error && <div className="analytics-error-banner">{error}</div>}

      {/* FILTER CONTROLS */}
      <div className="analytics-controls-card">
        <div className="filter-row">
          <div className="filter-group">
            <label>Date Range</label>
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              {DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {dateRange === "custom" && (
            <div className="filter-group custom-date-inputs">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
              <span>to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          )}

          <div className="filter-group">
            <label>Event</label>
            <select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
              <option value="all">All Events</option>
              {filterOptions.events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Platform</label>
            <select value={selectedPlatform} onChange={(e) => setSelectedPlatform(e.target.value)}>
              <option value="all">All Platforms</option>
              {filterOptions.platforms.map((p) => (
                <option key={p} value={p}>
                  {p.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Campaign</label>
            <select value={selectedCampaign} onChange={(e) => setSelectedCampaign(e.target.value)}>
              <option value="all">All Campaigns</option>
              {filterOptions.campaigns.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-actions-group">
            <button type="button" className="reset-filters-btn" onClick={handleResetFilters} title="Reset filters">
              <FaUndo /> <span>Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI METRIC CARDS */}
      <div className="analytics-kpi-grid">
        <div className="kpi-card blue">
          <div className="kpi-icon"><FaShareAlt /></div>
          <div className="kpi-content">
            <span className="kpi-label">Total Event Shares</span>
            <span className="kpi-value">{loading ? "..." : (stats?.sharesCount || 0) + (stats?.copiesCount || 0)}</span>
            <span className="kpi-hint">{stats?.sharesCount || 0} direct + {stats?.copiesCount || 0} copied</span>
          </div>
        </div>

        <div className="kpi-card green">
          <div className="kpi-icon"><FaEye /></div>
          <div className="kpi-content">
            <span className="kpi-label">Campaign Landings</span>
            <span className="kpi-value">{loading ? "..." : stats?.visitsCount || 0}</span>
            <span className="kpi-hint">Tracked via 9 UTM parameters</span>
          </div>
        </div>

        <div className="kpi-card purple">
          <div className="kpi-icon"><FaUsers /></div>
          <div className="kpi-content">
            <span className="kpi-label">Unique Visitors</span>
            <span className="kpi-value">{loading ? "..." : stats?.uniqueVisitors || 0}</span>
            <span className="kpi-hint">Privacy-safe unique IP hashes</span>
          </div>
        </div>

        <div className="kpi-card amber">
          <div className="kpi-icon"><FaAward /></div>
          <div className="kpi-content">
            <span className="kpi-label">Top Event</span>
            <span className="kpi-value small-text">{loading ? "..." : stats?.topContent?.name || "None yet"}</span>
            <span className="kpi-hint">{stats?.topContent?.count || 0} engagements</span>
          </div>
        </div>

        <div className="kpi-card cyan">
          <div className="kpi-icon"><FaBullhorn /></div>
          <div className="kpi-content">
            <span className="kpi-label">Top Platform</span>
            <span className="kpi-value">{loading ? "..." : stats?.topPlatform?.name?.toUpperCase() || "N/A"}</span>
            <span className="kpi-hint">{stats?.topPlatform?.count || 0} actions recorded</span>
          </div>
        </div>
      </div>

      {/* CHARTS ROW */}
      <div className="analytics-charts-grid">
        {/* TIMELINE */}
        <div className="chart-card full-width">
          <div className="chart-header">
            <h3>Event Traffic & Share Trends</h3>
            <span className="chart-sub">Timeline of direct shares, link copies, and campaign landings</span>
          </div>
          <div className="chart-body timeline-body">
            <Line
              data={timelineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "top" } },
                scales: {
                  y: { beginAtZero: true, grid: { color: "rgba(255, 255, 255, 0.05)" } },
                  x: { grid: { color: "rgba(255, 255, 255, 0.05)" } }
                }
              }}
            />
          </div>
        </div>

        {/* PLATFORM BREAKDOWN */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Platform Share Distribution</h3>
            <div className="metric-toggle">
              <button
                type="button"
                className={platformChartMetric === "all" ? "active" : ""}
                onClick={() => setPlatformChartMetric("all")}
              >
                All
              </button>
              <button
                type="button"
                className={platformChartMetric === "shares" ? "active" : ""}
                onClick={() => setPlatformChartMetric("shares")}
              >
                Shares
              </button>
              <button
                type="button"
                className={platformChartMetric === "visits" ? "active" : ""}
                onClick={() => setPlatformChartMetric("visits")}
              >
                Landings
              </button>
            </div>
          </div>
          <div className="chart-body doughnut-body">
            <Doughnut
              data={platformDoughnutData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "right" } }
              }}
            />
          </div>
        </div>

        {/* EVENT PERFORMANCE */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Top Performing Events</h3>
            <span className="chart-sub">Engagements by event</span>
          </div>
          <div className="chart-body bar-body">
            <Bar
              data={eventPerfData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "top" } },
                scales: {
                  y: { beginAtZero: true, grid: { color: "rgba(255, 255, 255, 0.05)" } },
                  x: { grid: { color: "rgba(255, 255, 255, 0.05)" } }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* DETAILED DATA TABLE */}
      <div className="analytics-table-card">
        <div className="table-card-header">
          <div>
            <h3>UTM Tracking Records</h3>
            <p>Aggregated records grouped by Event, Platform, and UTM campaign parameters.</p>
          </div>
          <div className="table-search-input">
            <FaSearch />
            <input
              type="text"
              placeholder="Search table records..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          <table className="analytics-data-table">
            <thead>
              <tr>
                <th>Event Title</th>
                <th>Platform</th>
                <th>UTM Campaign</th>
                <th>Source / Medium</th>
                <th>Shares</th>
                <th>Copies</th>
                <th>Landings</th>
                <th>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-table-td">
                    No UTM tracking records found for this filter range.
                  </td>
                </tr>
              ) : (
                tableData.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <b className="row-content-title">{row.eventTitle || row.title || "Event"}</b>
                    </td>
                    <td>
                      <span className={`platform-pill ${row.platform}`}>{row.platform}</span>
                    </td>
                    <td>
                      <code>{row.campaign || "event_share"}</code>
                    </td>
                    <td>
                      <span className="source-medium-text">
                        {row.source || "direct"} / {row.medium || "none"}
                      </span>
                    </td>
                    <td>{row.shareActions || 0}</td>
                    <td>{row.copyActions || 0}</td>
                    <td>
                      <b className="visits-highlight">{row.landingVisits || 0}</b>
                    </td>
                    <td>
                      <span className="last-seen-date">
                        {row.lastSeen ? new Date(row.lastSeen).toLocaleDateString("en-IN") : "—"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="table-pagination-footer">
            <div className="pagination-info">
              Showing page {pagination.page} of {pagination.totalPages} ({pagination.totalRows} total records)
            </div>
            <div className="pagination-buttons">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <span className="page-current">{pagination.page}</span>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventSharingAnalytics;
